// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useMembersForm } from '../hooks';
import { Error } from '../models/error';
import { fetchExistingAccounts } from '../api/accounts-private';
import css from '../shared/css';
import SimpleSelect from '../shared/simple-select';
import { emailTest } from '../utils/methods';

interface Props {
  rowID?: string;
  roleID: string;
  rowData: Row;
  removeRow: () => void;
  updateRow: (input: Row) => void;
  imported?: boolean;
  userRoles: Role[];
  defaultRole?: Role;
  isOnlyRow: boolean;
  isManaged: boolean;
  disabled: boolean;
  errors: Error[] | string[];
}

export interface Row {
  first_name: string;
  last_name: string;
  email: string;
  role?: Role;
  roles?: Role[];
  role_id: string;
  _id: string;
  valid?: boolean;
  imported?: boolean;
  existing_user?: boolean;
  errors?: Error[] | string[];
}

export interface Role {
  label: string;
  value: string;
  default: boolean;
}

export const AddMemberRow: React.FC<Props> = ({
  rowID,
  roleID,
  rowData,
  removeRow,
  updateRow,
  userRoles,
  defaultRole,
  isOnlyRow,
  isManaged,
  disabled,
  imported,
}: Props) => {
  const [data, setData] = useState<Row>({
    first_name: '',
    last_name: '',
    email: '',
    role: defaultRole,
    roles: [],
    role_id: roleID,
    _id: rowID,
    valid: false,
    imported: false,
    existing_user: false,
    errors: [],
  });

  const formSchema = {
    first_name: {
      value: '',
      error: '',
    },
    last_name: {
      value: '',
      error: '',
    },
    email: {
      value: '',
      error: '',
    },
  };

  const formValidationSchema = {
    first_name: {
      required: true,
    },
    last_name: {
      required: true,
    },
    email: {
      required: true,
      validator: {
        formatTest: (str: string) => emailTest(str),
        error: 'Invalid email format.',
      },
    },
  };

  const { state, handleOnChange, disable } = useMembersForm(
    formSchema,
    formValidationSchema,
    onSubmitForm,
  );

  // Sets the data once the form is valid
  function onSubmitForm() {
    setData({
      ...data,
      first_name: state.first_name.value,
      last_name: state.last_name.value,
      email: state.email.value,
      valid: true,
    });
  }

  // As a result of the data changing,
  // the row is updated to the parent modal
  useEffect(() => {
    updateRow(data);
  }, [data]);

  useEffect(() => {
    if (imported) {
      const errors = checkErrors(rowData.errors);

      state.first_name = {
        value: rowData.first_name,
        error: errors.first_name || '',
      };

      state.last_name = {
        value: rowData.last_name,
        error: errors.last_name || '',
      };

      state.email = {
        value: rowData.email,
        error: errors.email || '',
      };

      state.role = rowData.role;
      setData(rowData);
    }
  }, [imported]);

  // As a result of a row becoming disabled,
  // the button is disabled by setting the row false
  useEffect(() => {
    if (disable && data.valid) {
      setData({ ...data, valid: false });
    }
  }, [disable]);

  const handleRoleChange = (role: Role) => {
    setData({ ...data, role, role_id: role.value });
  };

  const clearErrors = () => {
    state.first_name.error = '';
    state.last_name.error = '';
    state.email.error = '';
  };

  const handleExistingAcct = async (email: string) => {
    const value = email.toLowerCase();

    if (emailTest(value)) {
      clearErrors();
      const account = await fetchExistingAccounts([value], isManaged);

      if (account.users.length) {
        const user = account.users[0];

        state.first_name.value = user.first_name;
        state.last_name.value = user.last_name;
        state.email.value = user.email;
        user.existing_user = true;
        user.valid = true;
        user.errors = [];

        setData({ ...data, ...user });
      }
    }
  };

  const checkErrors = (rowErrors) => {
    if (rowErrors.length) {
      const allErrors = [...rowErrors];
      return {
        first_name: allErrors.find((err) => err.startsWith('First Name')),
        last_name: allErrors.find((err) => err.startsWith('Last Name')),
        email: allErrors.find((err) => err.startsWith('Email')),
      };
    }
    rowErrors = {
      first_name: '',
      last_name: '',
      email: '',
    };

    return rowErrors;
  };

  const clearRow = () => {
    state.first_name.value = '';
    state.last_name.value = '';
    state.email.value = '';
    data.existing_user = false;
    data.valid = false;

    setData({ ...data, ...state });
  };

  const rowIsDirty = () => (
    state.first_name.value !== ''
    || state.last_name.value !== ''
    || state.email.value !== ''
  );

  const error = (input) => (input ? {
    border: '1px solid #eb1300',
  } : {});

  return (
    <div className={styles.wrapper}>
      <input
        autoFocus
        style={{ marginBottom: 0, ...error(state.email.error) }}
        className={styles.input}
        type="text"
        name="email"
        disabled={disabled}
        value={state.email.value}
        placeholder="name@example.com"
        autoComplete="off"
        data-testid="add-member-email-input"
        onKeyUp={() => handleExistingAcct(state.email.value)}
        onChange={handleOnChange}
      />
      <input
        style={error(state.first_name.error)}
        className={styles.input}
        type="text"
        name="first_name"
        disabled={disabled}
        value={state.first_name.value}
        placeholder="Enter a first name"
        autoComplete="off"
        onChange={handleOnChange}
      />
      <input
        style={error(state.last_name.error)}
        className={styles.input}
        type="text"
        name="last_name"
        disabled={disabled}
        value={state.last_name.value}
        placeholder="Enter a last name"
        autoComplete="off"
        onChange={handleOnChange}
      />
      <SimpleSelect
        options={userRoles}
        isClearable={false}
        backspaceRemovesValue={false}
        loadingMessage={() => 'Loading...'}
        onChange={(role: Role) => handleRoleChange(role)}
        menuPortal={document.body}
        value={state.role}
        placeholder="Select Role..."
      />
      {!isOnlyRow && (
        <button className={styles.removeButton} onClick={removeRow}>
          X
        </button>
      )}
      {isOnlyRow && rowIsDirty() && (
        <button className={styles.removeButton} onClick={clearRow}>
          X
        </button>
      )}
    </div>
  );
};

const styles = css({
  wrapper: {
    display: 'grid',
    gridTemplateColumns: '255px 185px 185px 163px 25px',
    alignItems: 'center',
    marginBottom: 3,
    gridGap: 3,
  },
  input: {
    width: 'unset',
    margin: '0',
  },
  removeButton: {
    height: 24,
    marginTop: 0,
    borderRadius: 12,
    fontWeight: 900,
    fontSize: 10,
    background: '#fff',
    border: '1px solid #bbb',
    color: '#999',
  },
  error: {
    border: '1px solid #eb1300 !important',
  },
});
